import React, { useState } from "react";
import { 
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from "@/components/ui/collapsible";
import { Card, CardContent } from "@/components/ui/card";
import { ChevronDown, ChevronUp, Video, Book, FileText } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { Button } from "@/components/ui/button";

interface LearningOutcome {
  text: string;
}

interface CurriculumModule {
  id: number;
  title: string;
  description: string;
  available: boolean;
  learningOutcomes?: LearningOutcome[];
}

interface CurriculumPart {
  title: string;
  modules: CurriculumModule[];
}

const CURRICULUM_DATA: CurriculumPart[] = [
  {
    title: "Part 1: Foundations of AI Governance",
    modules: [
      {
        id: 1,
        title: "Module 1: Demystifying AI – Core Concepts for Board Directors",
        description: "Understanding fundamental AI concepts and technologies including generative AI",
        available: true,
        learningOutcomes: [
          { text: "Understand key AI terminology and concepts relevant to board oversight" },
          { text: "Distinguish between different types of AI and their applications" },
          { text: "Recognize the unique characteristics of generative AI technologies" },
          { text: "Identify how AI differs from traditional software systems" }
        ]
      },
      {
        id: 2,
        title: "Module 2: AI as a Strategic Driver – Opportunities and Transformation",
        description: "Exploring AI's role in business transformation and strategic growth",
        available: false,
        learningOutcomes: [
          { text: "Evaluate AI's potential impact across different industries" },
          { text: "Identify strategic opportunities for AI implementation" },
          { text: "Assess how AI can enable new business models and capabilities" },
          { text: "Recognize patterns of successful AI transformation" }
        ]
      },
      {
        id: 3,
        title: "Module 3: The Global AI Landscape – Trends, Players, and Ecosystem",
        description: "Mapping the AI ecosystem and understanding key trends",
        available: false,
        learningOutcomes: [
          { text: "Understand the evolving global AI landscape" },
          { text: "Identify leading AI organizations and their strategic positions" },
          { text: "Recognize emerging trends and technologies" },
          { text: "Assess competitive implications for different industries" }
        ]
      },
      {
        id: 4,
        title: "Module 4: Identifying and Assessing AI Risks – A Board-Level Taxonomy",
        description: "Comprehensive framework for AI risk assessment and categorization",
        available: false,
        learningOutcomes: [
          { text: "Create a structured framework for assessing AI-related risks" },
          { text: "Identify ethical, regulatory, privacy, cybersecurity, operational, financial, and strategic risks" },
          { text: "Understand how different risks interconnect and compound" },
          { text: "Develop risk prioritization approaches for board-level oversight" }
        ]
      }
    ]
  },
  {
    title: "Part 2: Risk Management & Governance",
    modules: [
      {
        id: 5,
        title: "Module 5: Building Trustworthy AI – Ethical Frameworks and Explainability",
        description: "Implementing ethical AI principles and ensuring explainability",
        available: false,
        learningOutcomes: [
          { text: "Establish core ethical principles for AI development and use" },
          { text: "Create governance mechanisms to enforce ethical guidelines" },
          { text: "Understand explainable AI (XAI) approaches and their importance" },
          { text: "Develop strategies for ensuring AI transparency and accountability" }
        ]
      },
      {
        id: 6,
        title: "Module 6: The Evolving Global Regulatory Framework for AI",
        description: "Navigating the complex landscape of AI regulations worldwide",
        available: false,
        learningOutcomes: [
          { text: "Map the evolving landscape of AI regulations globally" },
          { text: "Identify key compliance requirements across jurisdictions" },
          { text: "Establish compliance monitoring processes for emerging regulations" },
          { text: "Prepare for regulatory audits and enforcement" }
        ]
      },
      {
        id: 7,
        title: "Module 7: Data Governance and Privacy – Critical Enablers for AI",
        description: "Building robust data frameworks to support responsible AI",
        available: false,
        learningOutcomes: [
          { text: "Design data governance frameworks specific to AI applications" },
          { text: "Implement data quality assessment processes" },
          { text: "Establish privacy-preserving data practices" },
          { text: "Create data access and usage policies" }
        ]
      },
      {
        id: 8,
        title: "Module 8: Securing AI – Addressing New Cybersecurity Challenges",
        description: "Understanding and mitigating unique security risks posed by AI",
        available: false,
        learningOutcomes: [
          { text: "Identify unique security risks in AI systems" },
          { text: "Implement adversarial testing protocols" },
          { text: "Establish model protection strategies" },
          { text: "Develop security incident response plans" }
        ]
      }
    ]
  },
  {
    title: "Part 3: Implementation & Board Responsibilities",
    modules: [
      {
        id: 9,
        title: "Module 9: The Board's AI Mandate – Fiduciary Duties and Core Obligations",
        description: "Understanding legal obligations and responsibilities for AI oversight",
        available: false,
        learningOutcomes: [
          { text: "Clarify board fiduciary duties related to AI governance" },
          { text: "Identify core oversight responsibilities for AI initiatives" },
          { text: "Understand legal and regulatory implications" },
          { text: "Define appropriate board-level risk appetites for AI" }
        ]
      },
      {
        id: 10,
        title: "Module 10: Structuring AI Governance – Committees, Charters, and Responsibilities",
        description: "Building effective organizational structures for AI oversight",
        available: false,
        learningOutcomes: [
          { text: "Design AI governance committees with clear mandates" },
          { text: "Establish reporting lines and escalation procedures" },
          { text: "Develop charter documents and operating guidelines" },
          { text: "Implement cross-functional governance mechanisms" }
        ]
      },
      {
        id: 11,
        title: "Module 11: Board Readiness – Cultivating AI Literacy and Expertise",
        description: "Developing board capabilities for effective AI oversight",
        available: false,
        learningOutcomes: [
          { text: "Assess current board AI literacy and expertise" },
          { text: "Create development plans for enhancing AI competencies" },
          { text: "Identify external expertise and partnership opportunities" },
          { text: "Establish continuous learning processes for evolving technologies" }
        ]
      },
      {
        id: 12,
        title: "Module 12: Preparing for the Unexpected – AI Incident Response and Board Role",
        description: "Creating effective response frameworks for AI incidents",
        available: false,
        learningOutcomes: [
          { text: "Develop AI-specific crisis response plans" },
          { text: "Create communications protocols for AI incidents" },
          { text: "Establish post-incident review processes" },
          { text: "Implement remediation tracking mechanisms" }
        ]
      }
    ]
  }
];

const ModuleCard: React.FC<{module: CurriculumModule}> = ({ module }) => {
  const [showDetails, setShowDetails] = useState(false);
  
  return (
    <Card className="border-2 border-gray-200 mb-6 shadow-sm hover:shadow-md transition-all duration-200">
      <CardContent className="p-6">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 mt-1">
            <div className="h-10 w-10 rounded-full bg-techne-purple/20 text-techne-purple flex items-center justify-center text-lg font-semibold">
              {module.id}
            </div>
          </div>
          
          <div className="space-y-3 w-full">
            <div>
              <h5 className="text-lg font-semibold text-gray-900">{module.title}</h5>
              <p className="text-base text-gray-700 mt-2">{module.description}</p>
            </div>
            
            {/* Module content types */}
            <div className="flex flex-wrap gap-3 mt-3">
              <div className="flex items-center text-sm bg-blue-100 text-blue-800 px-3 py-1.5 rounded-md">
                <Video className="h-4 w-4 mr-2" />
                <span>Video</span>
              </div>
              <div className="flex items-center text-sm bg-green-100 text-green-800 px-3 py-1.5 rounded-md">
                <Book className="h-4 w-4 mr-2" />
                <span>Readings</span>
              </div>
              <div className="flex items-center text-sm bg-purple-100 text-purple-800 px-3 py-1.5 rounded-md">
                <FileText className="h-4 w-4 mr-2" />
                <span>Templates</span>
              </div>
              <div className="flex items-center text-sm bg-amber-100 text-amber-800 px-3 py-1.5 rounded-md">
                <FileText className="h-4 w-4 mr-2" />
                <span>Checklist</span>
              </div>
            </div>
            
            <Button 
              variant="ghost" 
              onClick={() => setShowDetails(!showDetails)}
              className="text-techne-purple hover:text-techne-deepPurple hover:bg-techne-purple/10 mt-2 font-medium"
            >
              {showDetails ? "Hide details" : "Show details"}
              {showDetails ? <ChevronUp className="ml-2 h-4 w-4" /> : <ChevronDown className="ml-2 h-4 w-4" />}
            </Button>
            
            {showDetails && module.learningOutcomes && module.learningOutcomes.length > 0 && (
              <div className="mt-4 pt-3 border-t border-gray-200">
                <h6 className="text-base font-semibold mb-3">What you'll learn:</h6>
                <ul className="text-base text-gray-700 space-y-3 pl-2">
                  {module.learningOutcomes.map((outcome, idx) => (
                    <li key={idx} className="flex items-start">
                      <div className="h-5 w-5 rounded-full bg-green-100 text-green-700 flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                        ✓
                      </div>
                      <span>{outcome.text}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const AccordionCurriculumSection: React.FC = () => {
  const isMobile = useIsMobile();
  
  return (
    <section className="mb-20 px-4">
      <div className="text-center mb-10">
        <h2 className="text-3xl md:text-4xl font-bold mb-3">Course Curriculum</h2>
      </div>
      
      <div className={`grid ${isMobile ? 'grid-cols-1' : 'grid-cols-1'} gap-10 max-w-4xl mx-auto`}>
        {CURRICULUM_DATA.map((part, idx) => (
          <Collapsible key={idx} className="w-full">
            <div className="bg-techne-purple/10 rounded-xl p-4">
              <CollapsibleTrigger className="flex items-center justify-between w-full py-2 text-left hover:text-techne-purple transition-colors">
                <h3 className="text-xl md:text-2xl font-semibold text-techne-purple">{part.title}</h3>
                <ChevronDown className="h-6 w-6 text-techne-purple" />
              </CollapsibleTrigger>
            </div>
            
            <CollapsibleContent className="pt-6 pb-2 px-2">
              <div className="space-y-4">
                {part.modules.map(module => (
                  <ModuleCard key={module.id} module={module} />
                ))}
              </div>
            </CollapsibleContent>
          </Collapsible>
        ))}
      </div>
    </section>
  );
};

export default AccordionCurriculumSection;
